import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, BookOpen, Users, Send, Sparkles, Quote, Plus, X, Loader2, Image as ImageIcon } from "lucide-react";

// Fallback quote used only if the user has cleared every quote and the settings API returns an empty list.
const FALLBACK_QUOTE = { text: "Words are animals alive with a will of their own.", author: "Carl Jung" };

// Pick a stable quote for the current calendar day so it feels intentional, not random on refresh.
function pickDailyQuote(quotes) {
    if (!quotes || quotes.length === 0) return FALLBACK_QUOTE;
    const day = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
    return quotes[day % quotes.length];
}

export default function Dashboard() {
    const [stats, setStats] = useState({ editors: 0, projects: 0, resend_configured: false, llm: false });
    const [projects, setProjects] = useState([]);
    const [history, setHistory] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const quote = useMemo(() => pickDailyQuote(quotes), [quotes]);

    useEffect(() => {
        (async () => {
            try {
                const [h, p, hist, s] = await Promise.all([
                    api.get("/health").then((r) => r.data),
                    api.get("/projects").then((r) => r.data),
                    api.get("/history?limit=5").then((r) => r.data),
                    api.get("/settings").then((r) => r.data),
                ]);
                setStats(h);
                setProjects(p);
                setHistory(hist);
                setQuotes(Array.isArray(s.quotes) ? s.quotes : []);
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    return (
        <div>
            {/* Hero — vintage desk photograph with an inspiring literary quote */}
            <section
                className="relative overflow-hidden rounded-sm border border-border mb-10"
                data-testid="dashboard-hero"
                style={{ minHeight: "360px" }}
            >
                <img
                    src="/inspiration/desk.jpg"
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Warm parchment vignette over the photo so the type stays readable */}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(90deg, rgba(28,25,23,0.72) 0%, rgba(28,25,23,0.55) 45%, rgba(28,25,23,0.15) 100%)",
                    }}
                />
                <div className="relative z-10 p-10 sm:p-14 max-w-2xl text-[#F4F1EB]">
                    <div className="label-mono mb-3" style={{ color: "#DCD5C6" }}>
                        Studio · Red Fox Literary
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight">
                        Good work is quiet.
                    </h1>
                    <p className="mt-4 text-base sm:text-lg max-w-lg" style={{ color: "#E8E2D9" }}>
                        Match manuscripts to the editors most likely to champion them,
                        develop drafts with the author, and send letters that read like they were written for one person — because they were.
                    </p>

                    <blockquote className="mt-8 border-l-2 pl-4" style={{ borderColor: "#8C2D19" }}>
                        <Quote className="w-4 h-4 mb-1 opacity-70" strokeWidth={1.5} />
                        <p className="font-serif italic text-lg sm:text-xl leading-relaxed">
                            &ldquo;{quote.text}&rdquo;
                        </p>
                        <footer className="mt-2 label-mono" style={{ color: "#DCD5C6" }}>
                            {quote.author ? `— ${quote.author}` : ""}
                        </footer>
                    </blockquote>
                </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-border rounded-sm bg-white/60" data-testid="dashboard-stats">
                <Stat label="Editors on file" value={stats.editors} icon={Users} />
                <Stat label="Active projects" value={stats.projects} icon={BookOpen} border />
                <Stat label="Emails sent" value={history.length ? `${history.length}+` : "—"} icon={Send} border />
                <Stat
                    label="AI"
                    value={stats.llm ? "Ready" : "Off"}
                    icon={Sparkles}
                    border
                    tone={stats.llm ? "on" : "off"}
                />
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
                <div className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-serif text-2xl">Recent projects</h2>
                        <Link
                            to="/projects"
                            data-testid="dashboard-view-projects"
                            className="text-sm underline underline-offset-4 hover:text-[#8C2D19]"
                        >
                            View all
                        </Link>
                    </div>
                    <div className="paper border border-border rounded-sm divide-y divide-border" data-testid="recent-projects-list">
                        {projects.length === 0 && (
                            <div className="p-8 text-sm text-muted-foreground">
                                No projects yet. <Link to="/projects" className="underline">Start one →</Link>
                            </div>
                        )}
                        {projects.slice(0, 6).map((p) => (
                            <Link
                                key={p.id}
                                to={`/projects/${p.id}`}
                                data-testid={`project-row-${p.id}`}
                                className="flex items-center justify-between px-6 py-4 hover:bg-secondary/50 transition-colors"
                            >
                                <div>
                                    <div className="font-serif text-lg">{p.title}</div>
                                    <div className="label-mono mt-1">
                                        {[p.author, p.category, p.status, `R${p.current_round || 1}`].filter(Boolean).join(" · ") || "—"}
                                    </div>
                                </div>
                                <ArrowRight className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                            </Link>
                        ))}
                    </div>
                </div>

                <aside className="space-y-6">
                    {/* Journal / coffee photograph as a small ambient card */}
                    <div className="relative overflow-hidden rounded-sm border border-border" style={{ aspectRatio: "5/4" }} data-testid="ambient-photo">
                        <img
                            src="/inspiration/journal.jpg"
                            alt=""
                            aria-hidden
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(28,25,23,0) 55%, rgba(28,25,23,0.82) 100%)" }} />
                        <div className="absolute bottom-4 left-4 right-4" style={{ color: "#F4F1EB" }}>
                            <div className="label-mono" style={{ color: "#DCD5C6" }}>Today</div>
                            <div className="font-serif text-xl leading-tight mt-1">
                                A quiet room. A hot drink. The right sentence.
                            </div>
                        </div>
                    </div>

                    {/* Inspiration Board — user-uploaded images that are inspiring them right now */}
                    <InspirationBoard />

                    <div>
                        <h2 className="font-serif text-2xl mb-4">Latest sends</h2>
                        <div className="paper border border-border rounded-sm p-4 text-sm min-h-[120px]" data-testid="recent-history">
                            {history.length === 0 ? (
                                <div className="text-muted-foreground">No emails sent yet.</div>
                            ) : (
                                <ul className="divide-y divide-border">
                                    {history.map((h) => (
                                        <li key={h.id} className="py-3">
                                            <div className="text-foreground">{h.editor_name}</div>
                                            <div className="label-mono">{h.project_title}</div>
                                            <div className="text-xs text-muted-foreground mt-1 truncate">{h.subject}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}

function Stat({ label, value, icon: Icon, border, tone }) {
    return (
        <div className={"p-6 " + (border ? "border-t md:border-t-0 md:border-l border-border" : "")}>
            <div className="flex items-center justify-between">
                <div className="label-mono">{label}</div>
                <Icon
                    className={
                        "w-4 h-4 " +
                        (tone === "on" ? "text-[#1B362F]" : tone === "off" ? "text-[#8C2D19]" : "text-muted-foreground")
                    }
                    strokeWidth={1.5}
                />
            </div>
            <div className="font-serif text-4xl mt-3">{value}</div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// InspirationBoard — user-uploaded images shown as a soft asymmetric collage.
// Persists in Emergent Object Storage via /api/inspiration.
// ---------------------------------------------------------------------------
function InspirationBoard() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const backend = process.env.REACT_APP_BACKEND_URL || "";

    const load = async () => {
        setLoading(true);
        try {
            const list = await api.get("/inspiration").then((r) => r.data);
            setImages(Array.isArray(list) ? list : []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    // Global drop-navigation guard: without these, dragging a file onto the
    // browser window (anywhere OUTSIDE the board's drop zone) causes the
    // browser to navigate away and display the file as a plain page. We
    // suppress that default at the window level for the lifetime of the
    // Dashboard so the user never accidentally leaves the app.
    useEffect(() => {
        const swallow = (e) => {
            // Only swallow drops of FILES, not text/other drags (which some
            // drag-selection interactions rely on).
            if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files")) {
                e.preventDefault();
            }
        };
        window.addEventListener("dragover", swallow);
        window.addEventListener("drop", swallow);
        return () => {
            window.removeEventListener("dragover", swallow);
            window.removeEventListener("drop", swallow);
        };
    }, []);

    const upload = async (files) => {
        if (!files || !files.length) return;
        setUploading(true);
        try {
            let ok = 0;
            for (const f of files) {
                if (!f.type.startsWith("image/")) {
                    toast.error(`"${f.name}" isn't an image`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", f);
                try {
                    // The global axios instance has Content-Type: application/json
                    // as a default header (lib/api.js). For FormData we MUST let
                    // axios compute the multipart boundary — setting Content-Type
                    // to undefined removes the default and triggers auto-detection.
                    await api.post("/inspiration", fd, {
                        headers: { "Content-Type": undefined },
                    });
                    ok += 1;
                } catch (err) {
                    toast.error(err?.response?.data?.detail || `Failed to upload ${f.name}`);
                }
            }
            if (ok > 0) toast.success(`Added ${ok} image${ok === 1 ? "" : "s"}`);
            await load();
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    // Drag-and-drop: without these preventDefault handlers, the browser's
    // default behaviour is to navigate away to display the dropped file
    // (which looks to the user like the page "flashes to a blank background").
    const [dragOver, setDragOver] = useState(false);
    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    };
    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        const dropped = Array.from(e.dataTransfer?.files || []);
        if (dropped.length) upload(dropped);
    };

    const remove = async (id) => {
        // Optimistic
        const snapshot = images;
        setImages((cur) => cur.filter((i) => i.id !== id));
        try {
            await api.delete(`/inspiration/${id}`);
        } catch (e) {
            setImages(snapshot);
            toast.error("Couldn't remove that one");
        }
    };

    return (
        <div
            className={
                "paper border rounded-sm overflow-hidden transition-colors " +
                (dragOver ? "border-[#1B362F] bg-[#1B362F]/5" : "border-border")
            }
            data-testid="inspiration-board"
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            <div className="p-5 pb-3 flex items-center justify-between gap-3">
                <div>
                    <div className="label-mono">Inspiration board</div>
                    <p className="font-serif text-sm leading-snug mt-1 text-muted-foreground">
                        {images.length === 0 && !loading
                            ? "Drop images anywhere on this card, or click Add."
                            : dragOver
                                ? "Drop to add"
                                : "The things you're carrying with you right now."}
                    </p>
                </div>
                <label
                    className={"btn-ghost text-xs cursor-pointer shrink-0 " + (uploading ? "opacity-60 pointer-events-none" : "")}
                    title="Add images"
                    data-testid="inspiration-upload-label"
                >
                    {uploading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                    ) : (
                        <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                    )}
                    Add
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        multiple
                        className="hidden"
                        data-testid="inspiration-file-input"
                        onChange={(e) => upload(Array.from(e.target.files || []))}
                    />
                </label>
            </div>

            {loading ? (
                <div className="p-8 text-center text-xs font-mono text-muted-foreground">Loading…</div>
            ) : images.length === 0 ? (
                <label
                    className="block m-5 mt-2 border-2 border-dashed border-border rounded-sm p-10 text-center cursor-pointer hover:bg-secondary/50 transition-colors"
                    data-testid="inspiration-empty"
                >
                    <ImageIcon className="w-6 h-6 mx-auto text-muted-foreground mb-2" strokeWidth={1.5} />
                    <div className="font-serif text-sm">Click to add your first image</div>
                    <div className="label-mono mt-1">jpg, png, webp, gif · up to 10 MB</div>
                    <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        multiple
                        className="hidden"
                        onChange={(e) => upload(Array.from(e.target.files || []))}
                    />
                </label>
            ) : (
                <div className="p-3 pt-1 grid grid-cols-2 gap-2" data-testid="inspiration-grid">
                    {images.map((img, idx) => (
                        <div
                            key={img.id}
                            className={
                                "group relative overflow-hidden rounded-sm bg-secondary " +
                                // Every 5th image spans two columns for asymmetric rhythm
                                (idx % 5 === 0 && images.length >= 3 ? "col-span-2 aspect-[3/2]" : "aspect-square")
                            }
                            data-testid={`inspiration-item-${img.id}`}
                        >
                            <img
                                src={`${backend}${img.url}`}
                                alt={img.caption || img.filename || "Inspiration"}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <button
                                onClick={() => remove(img.id)}
                                className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove"
                                data-testid={`inspiration-remove-${img.id}`}
                            >
                                <X className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
